'use client'

import dynamic from 'next/dynamic'
import Link from 'next/link'
import { Box, Typography } from '@mui/material'

// Types
import type { PricingComparisonProps } from '@/types/components/organisms/pricing'

// Configurations
import Config from '@/config'
import PricingPlansConfig from '@/config/pricing-plans'

// Utils
import { localizeHref } from '@/utils/theme/nav-menu'
import { sharedPackageLimits } from '@/utils/package/limits'
import ToPersianNumber from '@/utils/number/to-persian-number'

// Components
const PrimaryButtonAtom = dynamic(() => import('@/components/atoms/button/primary'))
const PricingPriceCellMolecule = dynamic(() => import('@/components/molecules/pricing/price-cell'))
const PricingComparisonRowMolecule = dynamic(() => import('@/components/molecules/pricing/comparison-row'))

// The whole pricing in one table: per plan its price and action, the solutions (grouped as in the solutions menu,
// with a tick or cross) and the limits from the API. Follows the billing period chosen above it
const PricingComparisonOrganism = (props: PricingComparisonProps) => {
	// Props
	const { lng, locale, plans } = props

	// Variables
	const { title, solution, included, excluded, limits, custom, price } = locale.content.pricingPage.comparison
	const { enterprise } = locale.content.pricingPage
	const { groups, comingSoon } = locale.common.solutions
	const columns = [...plans.map(plan => plan.title), enterprise.title]
	const format = (value: number) =>
		value === -1 ? locale.common.title.unlimited : lng === 'fa' ? ToPersianNumber(new Intl.NumberFormat().format(value)) : new Intl.NumberFormat().format(value)

	// The cheapest plan is Basic and misses the advanced-only solutions; the rest and Enterprise have them all
	const solutionCells = (href: string) => [...plans.map((_, index) => index > 0 || !PricingPlansConfig.advancedOnly.includes(href)), true]

	// Zero means not included; Enterprise limits are set per customer
	const limitCells = (key: ReturnType<typeof sharedPackageLimits>[number]) => [...plans.map(plan => (plan[key] === 0 ? false : format(plan[key]))), custom]

	const rowTexts = { included, excluded }

	// Prices, then the actions: start in the app for the plans, get in touch for Enterprise
	const priceCells = [
		...plans.map(plan => <PricingPriceCellMolecule key={plan.alias} lng={lng} locale={locale} plan={plan} />),
		<PricingPriceCellMolecule key="enterprise" lng={lng} locale={locale} text={enterprise.price} />
	]

	const actionCells = [
		...plans.map(plan => (
			<Link key={plan.alias} href={Config.appUrl}>
				<PrimaryButtonAtom text={locale.form.button.getStarted} color="primary" size="small" />
			</Link>
		)),
		<Link key="enterprise" href={`/${lng}/contact-us`}>
			<PrimaryButtonAtom text={enterprise.action} color="inherit" size="small" />
		</Link>
	]

	return (
		<Box sx={{ mt: { xs: 4, md: 6 } }}>
			<Box
				role="table"
				aria-label={title}
				sx={{
					// Name, then one narrow column per plan
					'--pricing-columns': {
						xs: `minmax(0, 1fr) repeat(${columns.length}, 76px)`,
						md: `minmax(0, 1fr) repeat(${columns.length}, 200px)`
					},
					px: { xs: 2, md: 5 },
					pb: { xs: 2, md: 3 },
					borderRadius: { xs: 5, md: 8 },
					bgcolor: 'common.white'
				}}
			>
				{/* Column titles stay in view while scrolling the long list */}
				<Box
					role="row"
					sx={{
						display: 'grid',
						gridTemplateColumns: 'var(--pricing-columns)',
						alignItems: 'center',
						position: 'sticky',
						top: 88,
						zIndex: 1,
						py: { xs: 2, md: 2.5 },
						bgcolor: 'common.white',
						borderRadius: { xs: 5, md: 8 }
					}}
				>
					<Typography role="columnheader" sx={{ fontSize: 14, fontWeight: 700, color: 'text.secondary' }}>
						{solution}
					</Typography>

					{columns.map(column => (
						<Typography
							key={column}
							role="columnheader"
							sx={{ textAlign: 'center', fontSize: { xs: 12, md: 16 }, fontWeight: 700, lineHeight: 1.5, color: 'primary.dark' }}
						>
							{column}
						</Typography>
					))}
				</Box>

				<PricingComparisonRowMolecule title={price} cells={priceCells} {...rowTexts} />

				{/* Buttons fill their column; the label shrinks on phones */}
				<Box
					sx={{
						'& [role=cell] a, & [role=cell] .MuiButton-root': { display: 'block', width: 1 },
						'& [role=cell] .MuiButton-root': { px: 0.5 },
						'& [role=cell] .MuiButton-root .MuiTypography-root': { fontSize: { xs: 12, md: 15 } },
						'& [role=row]': { borderTop: 'none' }
					}}
				>
					<PricingComparisonRowMolecule title="" cells={actionCells} {...rowTexts} />
				</Box>

				{groups.map(group => (
					<Box key={group.title} role="rowgroup" sx={{ mt: 2 }}>
						<Typography role="row" sx={{ py: 1.5, fontSize: { xs: 15, md: 17 }, fontWeight: 700, color: 'text.primary' }}>
							<Box component="span" role="rowheader">
								{group.title}
							</Box>
						</Typography>

						{group.items.map(item => (
							<PricingComparisonRowMolecule
								key={item.href}
								title={item.title}
								href={localizeHref(lng, item.href)}
								badge={'soon' in item && item.soon ? comingSoon : undefined}
								cells={solutionCells(item.href)}
								{...rowTexts}
							/>
						))}
					</Box>
				))}

				<Box role="rowgroup" sx={{ mt: 2 }}>
					<Typography role="row" sx={{ py: 1.5, fontSize: { xs: 15, md: 17 }, fontWeight: 700, color: 'text.primary' }}>
						<Box component="span" role="rowheader">
							{limits}
						</Box>
					</Typography>

					{sharedPackageLimits(plans).map(key => (
						<PricingComparisonRowMolecule key={key} title={locale.form.label[key]} cells={limitCells(key)} {...rowTexts} />
					))}
				</Box>
			</Box>
		</Box>
	)
}

export default PricingComparisonOrganism
